/**
 * Renderer module: acquires a GPU backend (WebGPU, or three's automatic WebGL2
 * fallback) and hooks it into the frame loop's render phase.
 *
 * The three.js backend is loaded with a dynamic import, so three is its own
 * chunk and the boot screen paints before it is even downloaded (SPEC §10).
 * If no GPU API is available at all, the module falls back to the Null backend
 * instead of failing boot (architecture §2.2, R-1).
 */
import { describeQuality } from "./quality";
import { AssetsModule } from "../assets/AssetsModule";
import { CoreModule } from "../core/CoreModule";
import type { Log } from "../core/Log";
import { defineModule } from "../core/module";
import { NullRenderBackend } from "./NullRenderBackend";
import type {
  BackendContext,
  RenderBackend,
  RenderBackendFactory,
  RenderBackendInfo,
} from "./RenderBackend";
import type { ThreeBackendOptions } from "./three/ThreeRenderBackend";

export interface RendererOptions {
  readonly createBackend: RenderBackendFactory;
}

export const RendererModule = defineModule({
  name: "Renderer",
  dependsOn: [CoreModule, AssetsModule],
  options: (): RendererOptions => ({ createBackend: threeBackendFactory() }),
  init: async ({ deps: { Core, Assets }, options }) => {
    const backend = await acquireBackend(options, {
      assets: Assets.registry,
      requestFrame: (reason) => {
        Core.frameLoop.invalidate(reason);
      },
      warn: (category, message) => {
        Core.log.warning(category, message);
      },
      log: Core.log,
    });

    // Compile the level-independent pipelines (sky, floor, grid, post) before
    // the first level streams in, like an engine's shader cache warm-up.
    if (backend.info.api !== "Null") {
      Core.log.display("Shaders", "compiling pipeline cache");
      await backend.compile();
    }

    Core.frameLoop.register("render", (frame) => {
      backend.render(frame);
    });

    return { backend };
  },
  shutdown: ({ backend }) => {
    backend.dispose();
  },
});

/**
 * A factory that lazily loads the three.js backend. The dynamic import keeps
 * three out of the boot chunk, so the boot screen paints before it downloads.
 */
export function threeBackendFactory(options: ThreeBackendOptions = {}): RenderBackendFactory {
  return async (context) => {
    const { createThreeBackend } = await import("./three/ThreeRenderBackend");
    return createThreeBackend(options, context);
  };
}

/** Creates the configured backend, falling back to Null (with a warning) when no GPU API exists. */
async function acquireBackend(
  options: RendererOptions,
  { log, ...context }: BackendContext & { readonly log: Log },
): Promise<RenderBackend> {
  try {
    const backend = await options.createBackend(context);
    log.display("RHI", describeBackend(backend.info));
    if (backend.info.quality) log.display("Quality", describeQuality(backend.info.quality));
    return backend;
  } catch (thrown) {
    const reason = thrown instanceof Error ? thrown.message : String(thrown);
    log.warning("RHI", `no WebGPU or WebGL2 adapter (${reason}); viewport disabled`);
    return new NullRenderBackend();
  }
}

/** The boot screen's RHI line, from what was actually acquired. */
function describeBackend(info: RenderBackendInfo): string {
  const adapter = info.adapter ? ` (${info.adapter})` : "";
  switch (info.api) {
    case "WebGPU":
      return `WebGPU adapter acquired${adapter} · WebGL2 fallback available`;
    case "WebGL2":
      return `WebGPU unavailable · using WebGL2${adapter}`;
    case "Null":
      return "headless (no GPU backend)";
  }
}
